import * as React from 'react'
import AppHeader from '../../../../components/AppView/Header'
import { AppView, AppMainView } from '../../../../components/AppView'
import { Skeleton, message, Input, Row } from 'antd'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import { API, ICustomer, IPaginatedCustomer, PermissionNames } from '@getgreenline/homi-shared'
import { inject, observer } from 'mobx-react'
import { CurrentUserStore } from '../../../../stores/CurrentUserStore'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { parseErrorMsg } from '../../../../utilities/helpers'
import AppPagination from '../../../../components/Pagination/index'
import { CustomerTable } from './CustomerTable'
import { CustomerHeader } from '../Header/CustomerHeader'
import { CustomerViewAndEditModal } from '../../../../components/CustomerViewAndEditModal'
import { LocalStorage } from '../../../../utilities/LocalStorage'
import { LoadingAnimation } from '../../../../components/LoadingAnimation'
import { CustomersPageMFE } from '../../../../components/MfeParcels/CustomersPage'
import { openCustomerMFEModal } from '../../../../mfeEvents/customer'
import { CustomerSearch } from '@getgreenline/customer-search'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

interface State {
  query: string
  showAddCustomerModal: boolean
  selectedCustomer?: ICustomer | CustomerSearch.API.GlobalCustomer
  paginatedCustomers?: IPaginatedCustomer
  isLoading: boolean
  showSearched: boolean
  authToken?: string
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class CustomerList extends React.Component<Props, State> {
  company = this.props.currentCompanyStore!.company!

  pageLimit = 40

  constructor(props: Props) {
    super(props)

    this.state = {
      query: '',
      showAddCustomerModal: false,
      selectedCustomer: undefined,
      paginatedCustomers: undefined,
      isLoading: false,
      showSearched: false,
      authToken: undefined,
    }
  }

  componentDidMount = async () => {
    if (!this.props.currentCompanyStore?.sortedLoyaltyTiers) {
      await this.props.currentCompanyStore?.getLoyaltyTiers()
    }

    if (this.props.currentCompanyStore?.canUsePlatformCustomers === undefined) {
      await this.props.currentCompanyStore?.getCanUseLoyaltyService()
    }
    await this.props.currentCompanyStore!.getExternalLoyaltyProvider(this.company.id)

    if (this.props.currentCompanyStore?.canUsePlatformCustomers === undefined) {
      await this.props.currentCompanyStore!.getCanUsePlatformCustomersSearch()
    }

    if (this.props.currentCompanyStore?.canUsePlatformCustomers) {
      this.getAuthToken()
    } else {
      this.getCustomers()
    }
  }

  onPageChange = async ({ selected }: { selected: number }) => {
    try {
      this.state.query.length > 0
        ? await this.searchCustomers(selected * this.pageLimit)
        : await this.getCustomers(selected * this.pageLimit)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading customers')
    }
  }

  searchCustomers = async (offset = 0) => {
    this.setState({
      paginatedCustomers: undefined,
      isLoading: true,
    })

    try {
      const pagination = { limit: this.pageLimit, offset }
      const paginatedCustomers = await API.searchCustomers(
        this.company.id,
        this.state.query,
        pagination,
      )
      this.setState({ paginatedCustomers })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while searching for a customer')
    } finally {
      this.setState({ isLoading: false })
    }
  }

  getCustomers = async (offset = 0) => {
    this.setState({
      paginatedCustomers: undefined,
      isLoading: true,
    })

    try {
      const paginatedCustomers = await API.getCustomers(this.company.id, this.pageLimit, offset)
      await this.props.currentCompanyStore?.getEntityUsers()

      this.setState({ paginatedCustomers })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while retrieving the customer list')
    } finally {
      this.setState({ isLoading: false })
    }
  }

  getAuthToken = async () => {
    const authToken = await LocalStorage.getAuthToken()
    this.setState({
      authToken: authToken ?? undefined,
    })
  }

  onSearchByEnterKey = () => {
    const isQueryExist = this.state.query.trim().length > 0

    this.setState({ showSearched: isQueryExist })

    isQueryExist
      ? this.searchCustomers()
      : message.warn(`Please type customer's email, phone, or ID number`)
  }

  onRefresh = async () => {
    const { paginatedCustomers, query } = this.state
    const offset = paginatedCustomers ? paginatedCustomers.offset : 0
    query.length > 0 ? await this.searchCustomers(offset) : await this.getCustomers(offset)
  }

  onCustomerUndelete = () => {
    this.setState({
      showAddCustomerModal: false,
      selectedCustomer: undefined,
    })
    this.onRefresh()
  }

  get currentPage(): number {
    const { paginatedCustomers } = this.state

    if (!paginatedCustomers) {
      return 0
    }

    const { offset, limit } = paginatedCustomers
    const currPage = offset / limit

    return isNaN(currPage) ? 0 : currPage
  }

  get numberOfpages(): number {
    const { paginatedCustomers } = this.state

    if (!paginatedCustomers) {
      return 0
    }

    const { total, limit } = paginatedCustomers
    const pageCount = Math.ceil(total / limit)

    return isNaN(pageCount) ? 0 : pageCount
  }

  render() {
    const canAddCustomers = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_ADD_CUSTOMERS,
      this.company.id,
    )
    const canEditCustomers = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_CUSTOMERS,
      this.company.id,
    )

    const canViewCustomerTotalSpend = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_VIEW_CUSTOMER_TOTAL_SPEND,
      this.company.id,
    )

    const canUsePlatformCustomers = this.props.currentCompanyStore?.canUsePlatformCustomers
    const { query, showAddCustomerModal, isLoading, selectedCustomer, paginatedCustomers } =
      this.state

    const canAccessLoyalty =
      !this.props.currentCompanyStore?.canUseExternalLoyalty &&
      !!this.props.currentCompanyStore?.company?.permissions?.canAccessLoyalty

    return (
      <AppView column>
        <AppHeader title='Customers'>
          <CustomerHeader
            showAddCustomerModal={() =>
              this.setState({
                showAddCustomerModal: true,
                selectedCustomer: undefined,
              })
            }
            loadCustomers={this.onRefresh}
          />
        </AppHeader>

        <AppMainView className={`background-grey ${canUsePlatformCustomers ? '' : 'mb-4'}`}>
          {canUsePlatformCustomers === undefined ||
          this.props.currentCompanyStore?.sortedLoyaltyTiers === undefined ? (
            <LoadingAnimation />
          ) : (
            <>
              {canUsePlatformCustomers ? (
                <CustomersPageMFE
                  componentHeight='100%'
                  canViewCustomerTotalSpend={canViewCustomerTotalSpend}
                  canAccessLoyalty={canAccessLoyalty}
                  canEditCustomers={canEditCustomers}
                  companyId={this.company.id}
                  basename={`/company/${this.company?.id}/dashboard/customers/list`}
                  actions={{
                    onCustomerNameClick: (customer: CustomerSearch.API.GlobalCustomer) => {
                      this.setState({
                        showAddCustomerModal: true,
                        selectedCustomer: customer,
                      })
                    },
                    onDeleteCustomer: async (customer: CustomerSearch.API.GlobalCustomer) => {
                      try {
                        await API.deleteCustomer(this.company.id, customer.id)
                        openCustomerMFEModal({
                          message: 'Customer deleted successfully',
                          severity: 'success',
                        })
                      } catch (error) {
                        openCustomerMFEModal({
                          message:
                            parseErrorMsg(error) || 'An error occurred while deleting the customer',
                          severity: 'error',
                        })
                      }
                    },
                  }}
                  locations={this.props.currentCompanyStore?.locations || []}
                  loyaltyTiers={this.props.currentCompanyStore?.sortedLoyaltyTiers || []}
                />
              ) : (
                <>
                  <div className='d-flex flex-wrap space-evenly white-rounded-container pb-0'>
                    <Input
                      className='gr-filter-item gr-filter-gutter'
                      placeholder='Email, name, phone, ID'
                      value={query}
                      onChange={(e) => this.setState({ query: e.target.value })}
                      onPressEnter={this.onSearchByEnterKey}
                    />

                    <Row className='gr-filter-gutter'>
                      <BlazeButton
                        buttonProps={{
                          type: 'primary',
                          loading: isLoading,
                          onClick: () => {
                            this.searchCustomers()
                            this.setState({ showSearched: true })
                          },
                          disabled: !query.trim(),
                        }}
                        titleProps={{ title: 'Apply' }}
                      />

                      <BlazeButton
                        buttonProps={{
                          className: 'text-secondary',
                          type: 'link',
                          onClick: async () => {
                            this.setState({ query: '', showSearched: false })
                            await this.getCustomers()
                          },
                        }}
                        titleProps={{ title: 'Reset' }}
                      />
                    </Row>
                  </div>

                  {isLoading ? (
                    <Skeleton active />
                  ) : (
                    <CustomerTable
                      customers={paginatedCustomers ? paginatedCustomers.customers : []}
                      getCustomers={this.onRefresh}
                      showAddCustomerModal={(selectedCustomer: ICustomer) =>
                        this.setState({
                          showAddCustomerModal: true,
                          selectedCustomer,
                        })
                      }
                      highlightQuery={this.state.showSearched}
                      searchQuery={this.state.query}
                    />
                  )}
                </>
              )}
            </>
          )}
        </AppMainView>

        {canUsePlatformCustomers || canUsePlatformCustomers === undefined ? (
          <></>
        ) : (
          <AppPagination
            currentPage={this.currentPage}
            pageCount={this.numberOfpages}
            onPageChange={this.onPageChange}
          />
        )}

        {showAddCustomerModal && (
          <CustomerViewAndEditModal
            canViewCustomerTotalSpend={canViewCustomerTotalSpend}
            customerId={selectedCustomer ? selectedCustomer.id : undefined}
            canSaveCustomer={selectedCustomer ? canEditCustomers : canAddCustomers}
            onHide={() =>
              this.setState({
                showAddCustomerModal: false,
                selectedCustomer: undefined,
              })
            }
            onSubmit={() =>
              this.setState(
                {
                  showAddCustomerModal: false,
                  selectedCustomer: undefined,
                },
                async () => {
                  if (!canUsePlatformCustomers) {
                    this.getCustomers()
                  }
                },
              )
            }
            loadCustomers={this.onRefresh}
            onCustomerUndelete={this.onCustomerUndelete}
          />
        )}
      </AppView>
    )
  }
}
